import { useMemo } from 'react';
import { upperFirst } from 'lodash-es';
import { Tag } from '@care/react-component-lib';
import { Box, SxProps, type Theme } from '@mui/material';
import { getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_caregiverSearchResults_caregiver as Caregiver } from '@/__generated__/getSearchResults';
import shuffleCaregiverServices from '@/utils/shuffleCaregiverServices';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';

export interface CaregiverServicesProps {
  caregiver: Caregiver;
  sx?: SxProps<Theme>;
}

export const CaregiverServices = ({ sx, caregiver }: CaregiverServicesProps) => {
  const { profiles } = caregiver;
  const { shuffleSeed = 1 } = usePageAttributes();
  const MAX_LIST_LENGTH = 6;
  const supportedServices = profiles?.commonCaregiverProfile?.supportedServices ?? null;

  const services = useMemo(() => {
    const servicesList = shuffleCaregiverServices(supportedServices, shuffleSeed).map(
      (service, idx) => (idx === 0 ? upperFirst(service) : service)
    );

    if (servicesList.length >= MAX_LIST_LENGTH) {
      const overflowCount = servicesList.length - MAX_LIST_LENGTH + 1;
      servicesList.splice(MAX_LIST_LENGTH - 1);
      servicesList.push(`+ ${overflowCount} more`);
    }

    return servicesList;
  }, [supportedServices, shuffleSeed]);

  if (services.length === 0) {
    return null;
  }

  return (
    <Box sx={[...(Array.isArray(sx) ? sx : [sx])]}>
      {services.map((service) => (
        <Tag sx={{ mr: 1 }} key={service} type="grey" size="large">
          {service}
        </Tag>
      ))}
    </Box>
  );
};
